/**
 * Normalizes a JSON Pointer path and splits it into parts with RFC 6901 escape sequences decoded.
 *
 * @param dataPath - The RFC 6901 JSON Pointer path (e.g., "/a/b/c" or "/items/0").
 * @returns An object containing the normalized path parts and the first part separated from the rest.
 */
export const parsePathParts = (dataPath: string): { firstPart: string; restParts: string[] } => {
  const normalizedPath = dataPath.startsWith("/") ? dataPath.slice(1) : dataPath;
  const pathParts = normalizedPath.split("/").map((part) =>
    // Decode RFC 6901 escape sequences: ~1 -> /, ~0 -> ~
    part.replace(/~1/g, "/").replace(/~0/g, "~"),
  );

  const [firstPart, ...restParts] = pathParts;
  return { firstPart, restParts };
};

/**
 * Encodes a path part for use in an RFC 6901 JSON Pointer path.
 *
 * @param path - The path part to encode
 * @returns The path part with RFC 6901 escape sequences applied
 */
export const encodePath = (path: string): string => {
  return path.replace(/~/g, "~0").replace(/\//g, "~1");
};

/**
 * Reverses the operation of parsePathParts, joining the path parts into a normalized JSON Pointer path.
 *
 * @param parts - String array derived from parsePathParts representing the path parts
 * @returns The RFC 6901 Json pointer path
 */
export const joinPathParts = (parts: string[]): string => {
  return "/" + parts.map(encodePath).join("/");
};
